import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
  VtxCheckbox,
  VtxInputSearch,
  VtxRadio,
  VtxRadioGroup,
} from '@vertexinc/vtx-ui-react-component-library';
import SelectFilterProps, {
  getSelectFilterAsObject,
  getSelectFilterAsString,
  SelectFilterName,
  SelectFilterType,
} from './SelectFilterProps.interface';
import { ColumnFilterItem, FilterTypeEnum } from '../..';
import './select-filter.scss';

const SelectFilter = ({
  title,
  filterType = FilterTypeEnum.Text,
  multiple,
  dataIndex,
  convertBooleanToNumerical,
  props,
}: SelectFilterProps): JSX.Element => {
  const { selectedKeys, filters } = props;

  Logging.Debug('SelectFilter:', dataIndex, selectedKeys, filters);

  const [selectAll, setSelectAll] = React.useState(null);
  const [selectedFilterOption, setSelectedFilterOption] = React.useState(null); // Single Select Value
  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState(null); // Multi Select Values []
  const [filteredOptions, setFilteredOptions] = React.useState(null);
  const [searchValue, setSearchValue] = React.useState(null);

  // Check if Search Button is Enabled
  const searchButtonEnabled = multiple ? (selectedFilterOptions?.length > 0) : (selectedFilterOption !== null);

  // Check if Clear Button is Enabled
  const clearButtonEnabled = true;

  // Clear Button Pressed
  const onClear = (): void => {
    props.clearFilters();
  };

  // Search Button Pressed - Save the Select Filter as a JSON Object
  const onSearch = (): void => {
    // Filter is not configured.  Clear it.
    if (multiple && !searchValue && selectAll) {
      onClear();

      return;
    }

    // Get the Select Filter as a stringified JSON Object
    const searchFilterString: string = getSelectFilterAsString(
      filterType,
      searchValue,
      multiple ? selectAll : null,
      multiple ? selectedFilterOptions : [selectedFilterOption],
      multiple
        ? filters.filter((y) => selectedFilterOptions.includes(y.value)).map((x) => x.text.toString())
        : [filters.filter((y) => y.value.toString() === selectedFilterOption.toString())[0].text.toString()],
      convertBooleanToNumerical,
    );

    props.setSelectedKeys([searchFilterString]);
    Logging.Debug('SetSelectedKeys:', [searchFilterString]);

    props.confirm();
  };

  // Update the Selection Array
  const updateArray = (data: string, value: boolean, values: string[]): string[] => {
    let copyValues = [...values];

    if (value) {
      copyValues.push(data);
    } else {
      copyValues = copyValues.filter((item) => item !== data);
    }

    return copyValues;
  };

  // Checkbox Value has Changed (Multi)
  const onChangeCheckboxMultiple = (
    event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    data: string,
    onChangeValue?: React.Dispatch<React.SetStateAction<string[]>>,
  ): void => {
    if (onChangeValue) {
      const value: boolean = event.target.checked;
      Logging.Debug('onChangeCheckboxMultiple', event.target.id, data, value);

      onChangeValue((previousArray) => updateArray(data, value, previousArray));

      // If any option is toggled, disable the Select All
      setSelectAll(false);
    }
  };

  // Checkbox Value has Changed (Single)
  const onChangeCheckbox = (
    event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    onChangeValue?: React.Dispatch<React.SetStateAction<boolean>>,
  ): void => {
    if (onChangeValue) {
      const value: boolean = event.target.checked;
      Logging.Debug('onChangeCheckbox', event.target.id, value);
      onChangeValue(value);
    }
  };

  // Render Checkbox
  const renderCheckbox = (
    id: string,
    name: React.ReactNode,
    value: boolean,
    data: string,
    onChange: React.Dispatch<React.SetStateAction<boolean | string[]>>,
  ): JSX.Element => {
    Logging.Debug('renderInput', id, value);

    return (
      <div key={id} className="fieldBase">
        <VtxCheckbox
          id={id}
          checked={value}
          disabled={false}
          onChange={(event): void => data // eslint-disable-line no-confusing-arrow
            ? onChangeCheckboxMultiple(event, data, onChange as React.Dispatch<React.SetStateAction<string[]>>)
            : onChangeCheckbox(event, onChange as React.Dispatch<React.SetStateAction<boolean>>)}
        >
          {name}
        </VtxCheckbox>
      </div>
    );
  };

  // Render Checkboxes
  const renderCheckboxes = (): JSX.Element[] => {
    Logging.Debug('Checkboxes');

    return filteredOptions?.map((item: ColumnFilterItem) => {
      const data = item.value.toString();
      const display = item.text;

      return (renderCheckbox(data,
        display,
        selectedFilterOptions ? selectedFilterOptions.indexOf(data) !== -1 : false,
        data,
        setSelectedFilterOptions));
    });
  };

  // Set the Selected Filter Options
  const selectAllFilterOptions = (options: ColumnFilterItem[]): void => {
    const values: string[] = [];

    if (options) {
      options.forEach((item: ColumnFilterItem) => {
        values.push(item.value.toString());
      });
    }

    setSelectedFilterOptions(values);
  };

  // On Search
  const onSearchChange = (searchText: string) => {
    Logging.Debug('onSearchChange', searchText);

    setSearchValue(searchText);

    // Update Filtered Options
    const filtered = searchText ? filters.filter((option) => option.text.toString().toLowerCase().includes(searchText.toLowerCase())) : filters;
    setFilteredOptions(filtered);

    // Multi Mode
    if (multiple) {
      // Set Select All to True and select the new Filtered Options.  If no Filtered Options, select None
      setSelectAll(filtered.length > 0);
      selectAllFilterOptions(filtered);
    } else { // Single Mode
      // Clear the Selection if Single Mode
      setSelectedFilterOption(null);
    }
  };

  // Render Radio
  const renderRadios = (): JSX.Element[] => {
    Logging.Debug('Radio');

    return filteredOptions?.map((item: ColumnFilterItem) => {
      const data = item.value.toString();
      const display = item.text;

      return (
        <div key={data} className="fieldBase selectFilterOption">
          <VtxRadio value={data}>{display}</VtxRadio>
        </div>
      );
    });
  };

  // Radio Button Changed
  const onChangeRadio = (filterOption: string): void => {
    Logging.Debug('onChangeRadio', filterOption);

    setSelectedFilterOption(filterOption);
  };

  // Select All Checkbox was Toggled
  const selectAllChanged = (value: boolean): void => {
    Logging.Debug('Select All Changed:', value);

    // Select all the Values
    if (value === true) {
      selectAllFilterOptions(filteredOptions);
    } else { // Clear the Selected Filter Options
      setSelectedFilterOptions([]);
    }

    setSelectAll(value);
  };

  // Create a Default Select Filter
  const getDefaultSelectFilter = (): SelectFilterType => {
    const selectFilter: SelectFilterType = {
      name: SelectFilterName,
      version: 1,
      type: filterType,
      search: null,
      selectAll: true,
      values: [],
      displayValues: [],
      convertBooleanToNumerical: false,
    };

    return selectFilter;
  };

  // Selected Keys has Changed
  React.useEffect(() => {
    // If specified use the Search Filter Object, otherwise use Defaults
    const selectFilter: SelectFilterType = selectedKeys && selectedKeys[0]
      ? getSelectFilterAsObject(selectedKeys[0] as string)
      : getDefaultSelectFilter();

    // Search Text
    const searchText = selectFilter.search;
    setSearchValue(searchText);

    // Filtered Options
    const filtered = searchText ? filters.filter((option) => option.text.toString().toLowerCase().includes(searchText.toLowerCase())) : filters;
    setFilteredOptions(filtered);

    // Multi Select
    if (multiple) {
      // Select All
      setSelectAll(selectFilter.selectAll);

      // Select All = True (Select all the Values)
      if (selectFilter.selectAll) {
        selectAllFilterOptions(filtered);
      } else { // Select All = False (Select only Specific Selected Values)
        setSelectedFilterOptions(selectFilter.values);
      }
    } else { // Single Select
      const filterOption = selectFilter.values[0] || null;
      setSelectedFilterOption(filterOption);
    }
  }, [selectedKeys]);

  return (
    <div className="selectFilter">
      {
        title
          && (
            <div className="fieldBase selectFilterTitle">
              {title} Filter
            </div>
          )
      }
      <div className="fieldBase selectFilterSearch">
        <VtxInputSearch
          type="search"
          placeholder="Filter ..."
          allowClear
          value={searchValue}
          onSearch={onSearchChange}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      {
        multiple
          ? (
            <>
              <div className="selectFilterSelectAll">
                {renderCheckbox('selectAll', '(Select All)', selectAll, null, selectAllChanged)}
              </div>
              <div className="selectFilterOptions">
                {renderCheckboxes()}
              </div>
            </>
          )
          : (
            <div className="selectFilterOptions">
              <VtxRadioGroup
                defaultValue={null}
                value={selectedFilterOption}
                onChange={(event): void => onChangeRadio(event.target.value)}
                name="filterOptionGroup"
              >
                {renderRadios()}
              </VtxRadioGroup>
            </div>
          )
      }
      {
        (filteredOptions && (filteredOptions.length === 0))
          && (
            <div className="fieldBase">
              No matches.
            </div>
          )
      }
      <div className="selectLine" />
      <div className="filterButtons">
        <div className="filterButtonBase">
          <VtxButton
            id="searchButton"
            onClick={onSearch}
            type="primary"
            disabled={!searchButtonEnabled}
          >
            Filter
          </VtxButton>
        </div>
        <div className="filterButtonBase">
          <VtxButton
            id="clearButton"
            onClick={onClear}
            type="default"
            disabled={!clearButtonEnabled}
          >
            Clear
          </VtxButton>
        </div>
      </div>
    </div>
  );
};

export default SelectFilter;
